import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import './Toast.css';

/**
 * Toast Notification Component
 *
 * Displays temporary notification messages with automatic dismissal.
 * Supports 4 types: success, error, warning, info
 *
 * Usage:
 * import { toast } from './Toast';
 * toast.success('Operation completed!');
 * toast.error('Something went wrong');
 */

let toastId = 0;
const listeners = [];

// eslint-disable-next-line react-refresh/only-export-components
export const toast = {
  success: (message, options = {}) => showToast('success', message, options),
  error: (message, options = {}) => showToast('error', message, options),
  warning: (message, options = {}) => showToast('warning', message, options),
  info: (message, options = {}) => showToast('info', message, options),
};

function showToast(type, message, options) {
  const id = ++toastId;
  const notification = {
    id,
    type,
    message,
    duration: options.duration || 5000,
    action: options.action,
  };

  listeners.forEach(listener => listener(notification));

  return id;
}

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const listener = (notification) => {
      setToasts(prev => [...prev, notification]);

      // Auto-remove after duration
      if (notification.duration > 0) {
        setTimeout(() => {
          removeToast(notification.id);
        }, notification.duration);
      }
    };

    listeners.push(listener);

    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="toast-icon" />;
      case 'error':
        return <XCircle className="toast-icon" />;
      case 'warning':
        return <AlertCircle className="toast-icon" />;
      case 'info':
      default:
        return <Info className="toast-icon" />;
    }
  };

  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.type} relative overflow-hidden`}
          role="alert"
          aria-live="polite"
        >
          <div className="toast-content">
            <div className="toast-icon-wrapper">
              {getIcon(t.type)}
            </div>
            <div className="toast-message">{t.message}</div>
          </div>
          {t.action && (
            <button
              className="toast-action"
              onClick={() => {
                t.action.onClick();
                removeToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button
            className="toast-close"
            onClick={() => removeToast(t.id)}
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
          {/* Progress bar */}
          {t.duration > 0 && (
            <div
              className="toast-progress"
              style={{ animationDuration: `${t.duration}ms` }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
